import React, {useCallback, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { Button, Form, Input, Typography, message } from "antd";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import ModernAuthLayout from "../components/Auth/ModernAuthLayout";
import { TwoFactorLogin } from "../components/Auth/TwoFactorLogin";

import {ExclamationCircleOutlined, LockOutlined, MailOutlined} from "@ant-design/icons";
import {useGetProfileMutation, useLoginMutation, useGetSignupConfigQuery} from "../services/nestjs-base-api";
import { useCharterConfig } from "../hooks/useCharterConfig";

const { Text, Title, Link } = Typography;

const SigninPage: React.FunctionComponent = () => {
    const { login } = useAuth();
    const intl = useIntl();
    const { charterCohortEnabled } = useCharterConfig();
    // SYR-109: signup and the Charter intake can be open at once. Signup is
    // the primary door whenever it is open; Charter takes the line only when
    // signup is closed, and otherwise becomes a quiet second line.
    const { data: signupConfig } = useGetSignupConfigQuery();
    const charterPrimary = charterCohortEnabled && signupConfig?.publicSignupEnabled !== true;
    const [searchParams] = useSearchParams();
    const redirectPath = searchParams.get('redirect');
    const [isMobile, setIsMobile] = React.useState(false);
    const [showTwoFactor, setShowTwoFactor] = useState(false);
    const [credentials, setCredentials] = useState<{ email: string; password: string; extendedExpiry?: boolean } | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [twoFactorError, setTwoFactorError] = useState('');

    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    const [apiLogin, apiLoginResult] = useLoginMutation();
    const [getProfile, apiGetProfileResult] = useGetProfileMutation();
    const [messageApi, contextHolder] = message.useMessage();

    const onLoginError = useCallback(() => {
        messageApi.open({
            type: 'error',
            content: intl.formatMessage({ id: 'auth.signin.invalidCredentials' }),
            duration: 4,
            icon: <ExclamationCircleOutlined />
        });
    }, [messageApi, intl]);

    const handleLogin = useCallback(async (values: any) => {
        const authResponse = await apiLogin({
            email: values.email, 
            password: values.password
        });

        if ('data' in authResponse) {
            // Check if 2FA setup is required
            if (authResponse.data.requires_2fa_setup) {
                // Get profile with limited token and redirect to 2FA setup
                const profileResponse = await getProfile({jwtToken: authResponse.data.access_token});
                
                if ('data' in profileResponse) {
                    // Store token separately for API calls
                    localStorage.setItem('token', authResponse.data.access_token);
                    // For 2FA setup, always redirect to security tab (ignore redirectPath)
                    login({...profileResponse.data, jwtToken: authResponse.data.access_token});
                    // Redirect to security tab after login
                    setTimeout(() => {
                        window.location.href = '/admin/profile?tab=security';
                    }, 100);
                }
                return;
            }
            
            // Check if 2FA is required
            if (authResponse.data.requires_2fa) {
                setCredentials({ 
                    email: values.email, 
                    password: values.password
                });
                setIsAdmin(authResponse.data.isAdmin || false);
                setShowTwoFactor(true);
                return;
            }

            // Normal login flow
            const profileResponse = await getProfile({jwtToken: authResponse.data.access_token});

            if ('data' in profileResponse) {
                // Store token separately for API calls
                localStorage.setItem('token', authResponse.data.access_token);
                login({...profileResponse.data, jwtToken: authResponse.data.access_token}, redirectPath || undefined);
            }
        } else {
            onLoginError();
        }
    }, [apiLogin, getProfile, login, onLoginError, redirectPath]);

    const handleTwoFactorVerify = useCallback(async (token: string, extendedExpiry?: boolean) => {
        if (!credentials) return;

        try {
            setTwoFactorError('');
            const authResponse = await apiLogin({
                email: credentials.email, 
                password: credentials.password, 
                twoFactorToken: token,
                extendedExpiry: extendedExpiry || false
            });

            if ('data' in authResponse && authResponse.data.access_token) {
                const profileResponse = await getProfile({jwtToken: authResponse.data.access_token});

                if ('data' in profileResponse) {
                    // Store token separately for API calls (includes 30-day expiry for admins with extendedExpiry)
                    localStorage.setItem('token', authResponse.data.access_token);
                    login({...profileResponse.data, jwtToken: authResponse.data.access_token}, redirectPath || undefined);
                }
            } else {
                setTwoFactorError('Invalid verification code. Please try again.');
            }
        } catch (error) {
            setTwoFactorError('Invalid verification code. Please try again.');
        }
    }, [credentials, apiLogin, getProfile, login, redirectPath]);

    const handleTwoFactorBack = useCallback(() => {
        setShowTwoFactor(false);
        setCredentials(null);
        setTwoFactorError('');
    }, []);

    // Show 2FA form if required
    if (showTwoFactor) {
        return (
            <ModernAuthLayout>
                {contextHolder}
                <TwoFactorLogin
                    onVerify={handleTwoFactorVerify}
                    onBack={handleTwoFactorBack}
                    isLoading={apiLoginResult.isLoading || apiGetProfileResult.isLoading}
                    error={twoFactorError}
                    isAdmin={isAdmin}
                />
            </ModernAuthLayout>
        );
    }

    return (
        <ModernAuthLayout>
            {contextHolder}
            <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
                <Title level={2} style={{ color: '#1f2937', fontWeight: '700', marginBottom: '0.5rem', fontSize: isMobile ? '24px' : '30px' }}>
                    {intl.formatMessage({ id: 'auth.signin.title' })}
                </Title>
                <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
                    {intl.formatMessage({ id: 'auth.signin.welcomeText' })}
                </Text>
            </div>

            <Form
                name="signin_form"
                onFinish={handleLogin}
                layout="vertical"
                requiredMark={false}
                size="large"
            >
                <Form.Item
                    name="email"
                    label={<span style={{ fontWeight: '600', color: '#374151' }}>{intl.formatMessage({ id: 'auth.signin.label.email' })}</span>}
                    rules={[
                        {
                            type: "email",
                            required: true,
                            message: intl.formatMessage({ id: 'auth.signin.emailRequired' }),
                        },
                    ]}
                >
                    <Input
                        prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
                        placeholder={intl.formatMessage({ id: 'auth.signin.emailPlaceholder' })}
                        style={{ 
                            height: isMobile ? '52px' : '48px',
                            borderRadius: '12px',
                            border: '2px solid #e5e7eb',
                            fontSize: isMobile ? '16px' : '16px'
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="password"
                    label={<span style={{ fontWeight: '600', color: '#374151' }}>{intl.formatMessage({ id: 'auth.signin.label.password' })}</span>}
                    rules={[
                        {
                            required: true,
                            message: intl.formatMessage({ id: 'auth.signin.passwordRequired' }),
                        },
                    ]}
                >
                    <Input.Password
                        prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                        placeholder={intl.formatMessage({ id: 'auth.signin.passwordPlaceholder' })}
                        style={{ 
                            height: isMobile ? '52px' : '48px',
                            borderRadius: '12px',
                            border: '2px solid #e5e7eb',
                            fontSize: isMobile ? '16px' : '16px'
                        }}
                    />
                </Form.Item>

                <Form.Item>
                    <div style={{ textAlign: 'right', marginBottom: isMobile ? '1rem' : '1.5rem' }}>
                        <Link href="/forgot" style={{ color: '#F59E0B', fontWeight: '500', fontSize: isMobile ? '14px' : '16px' }}>
                            {intl.formatMessage({ id: 'auth.signin.forgotPassword' })}
                        </Link>
                    </div>
                </Form.Item>

                <Form.Item>
                    <Button
                        block
                        type="primary"
                        htmlType="submit"
                        loading={apiLoginResult.isLoading || apiGetProfileResult.isLoading}
                        style={{
                            height: isMobile ? '52px' : '48px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                            border: 'none',
                            fontSize: isMobile ? '16px' : '16px',
                            fontWeight: '600',
                            boxShadow: '0 4px 14px 0 rgba(245, 158, 11, 0.4)'
                        }}
                    >
                        {intl.formatMessage({ id: 'auth.signin.title' })}
                    </Button>
                </Form.Item>

                <div style={{ textAlign: 'center', marginTop: isMobile ? '1rem' : '1.5rem' }}>
                    <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
                        {intl.formatMessage({ id: charterPrimary ? 'auth.signin.noAccountCharter' : 'auth.signin.noAccount' })}
                    </Text>
                    {" "}
                    <Link href={charterPrimary ? '/charter' : '/sign-up'} style={{ color: '#F59E0B', fontWeight: '600', fontSize: isMobile ? '14px' : '16px' }}>
                        {intl.formatMessage({ id: charterPrimary ? 'auth.signin.charterLink' : 'auth.signin.signupLink' })}
                    </Link>
                    {charterCohortEnabled && !charterPrimary && (
                        <div style={{ marginTop: '0.5rem' }}>
                            <Link href="/charter" style={{ color: '#F59E0B', fontSize: isMobile ? '13px' : '14px' }}>
                                {intl.formatMessage({ id: 'auth.signin.charterSecondary' })}
                            </Link>
                        </div>
                    )}
                </div>
            </Form>
        </ModernAuthLayout>
    );
}

export default SigninPage;
